import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

/* index.html ships static description/og/twitter/canonical tags as a
   fallback for the pre-hydration flash and for bots that don't execute JS
   (link-preview crawlers). Once React mounts, every route renders its own
   <SEO>, so these static duplicates (recognizable by lacking Helmet's
   data-rh marker) are stale and would sit alongside the live ones forever
   — clean them up once real tags land. */
function cleanupStaticFallbackTags() {
  document
    .querySelectorAll(
      'meta[name="description"]:not([data-rh]), meta[name="keywords"]:not([data-rh]), meta[property^="og:"]:not([data-rh]), meta[name^="twitter:"]:not([data-rh]), link[rel="canonical"]:not([data-rh]), link[rel="alternate"][hreflang]:not([data-rh])'
    )
    .forEach((el) => el.remove());
}

const SITE_URL = "https://greenwich.am";
const DEFAULT_IMAGE = `${SITE_URL}/nkarner/acf56a2df45f48f796f9d09c2c7448b2.jpg`;

/**
 * Per-page SEO tags: title, description, keywords, canonical, hreflang,
 * Open Graph, Twitter Card and optional JSON-LD structured data.
 * `path` must be the route path (e.g. "/programs") without locale prefix —
 * the app doesn't use locale-prefixed URLs, so hreflang alternates all
 * point at the same URL with different `og:locale`/`lang` hints.
 */
export default function SEO({ titleKey, descriptionKey, keywordsKey, path = "/", image, jsonLd, breadcrumbLabel, noindex = false, overrideTitle, overrideDescription }) {
  const { t, i18n } = useTranslation();

  // overrideTitle/overrideDescription are for dynamic, already-in-whatever-
  // language content (e.g. a specific program's own title/description) that
  // isn't an i18n key at all — titleKey/descriptionKey stay required props
  // on every <SEO> call site, so they're always available as the fallback.
  const title = overrideTitle || t(titleKey);
  const description = overrideDescription || t(descriptionKey);
  const keywords = keywordsKey ? t(keywordsKey) : undefined;
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  const ogImage = image || DEFAULT_IMAGE;
  const lang = i18n.language === "hy" ? "hy_AM" : i18n.language === "ru" ? "ru_RU" : "en_US";

  const breadcrumbLd = breadcrumbLabel && path !== "/" ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Greenwich", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: breadcrumbLabel, item: url },
    ],
  } : null;

  const jsonLdBlocks = [
    ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
    ...(breadcrumbLd ? [breadcrumbLd] : []),
  ];

  useEffect(() => {
    const raf = requestAnimationFrame(cleanupStaticFallbackTags);
    return () => cancelAnimationFrame(raf);
  }, [title, description, url]);

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="hy" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Greenwich" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={lang} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}

export { SITE_URL };
